import { Chip, Tooltip } from '@mui/material';
import {
    GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
    GridActionsCellItem,
    GridActionsCellItemProps
} from '@mui/x-data-grid-premium';
import React, { ReactElement } from 'react';
import type { EntitySecurityEvaluator } from '../../auth/Security';
import { SECURITY_ACTIONS } from '../../auth/Security';
import { SIZES } from '../../constants/constants';
import I18n, { I18nFunctionType } from '../../i18n/I18n';
import { Icons } from '../../icons/Icons';
import type { DataRowSecurityActionPermissions } from '../types';
import { filterCustomRowActionsForVisibilityAndSecurity } from './filterCustomRowActionsForVisibilityAndSecurity';
import type { UseDataGridActionsParams } from './types';

const CHECK_ICON = <Icons.Check fontSize={SIZES.small} />;
const CLOSE_ICON = <Icons.Close fontSize={SIZES.small} />;
const EDIT_ICON = <Icons.Edit fontSize={SIZES.small} />;
const COPY_ICON = <Icons.ContentCopy fontSize={SIZES.small} />;
const DELETE_ICON = <Icons.Delete fontSize={SIZES.small} />;
const SHARE_ICON = <Icons.Share fontSize={SIZES.small} />;

export type GetDataGridActionsProps = Pick<
    UseDataGridActionsParams,
    | 'params'
    | 'entityName'
    | 'onActivateOrInactivateHandle'
    | 'rowDetails'
    | 'editHandlers'
    | 'onShare'
    | 'deleteHandlers'
    | 'copyHandlers'
    | 'customActions'
    | 'customRowActions'
    | 'isMobile'
    | 'actionLabels'
> & {
    resolvedRowPermissions: DataRowSecurityActionPermissions;
    evaluateSecurity: EntitySecurityEvaluator;
    shouldUseCentralizedSecurityFramework: boolean;
    translateLabelToken: I18nFunctionType;
    hideEditActionWhenInlineEditing?: boolean;
};

export const getDataGridActions = ({
    params,
    entityName,
    resolvedRowPermissions,
    evaluateSecurity,
    shouldUseCentralizedSecurityFramework,
    onActivateOrInactivateHandle,
    rowDetails,
    editHandlers,
    onShare,
    deleteHandlers,
    copyHandlers,
    customActions,
    customRowActions = [],
    isMobile,
    translateLabelToken,
    actionLabels: { saveLabel, cancelLabel, editLabel, shareLabel, copyLabel, deleteLabel },
    hideEditActionWhenInlineEditing = false
}: GetDataGridActionsProps) => {
    if (params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
        return [];
    }

    const isInEditMode = !!rowDetails.activeRow && params.id === rowDetails.activeRow.id;

    const canEdit = resolvedRowPermissions[SECURITY_ACTIONS.EDIT];
    const canShare = resolvedRowPermissions[SECURITY_ACTIONS.SHARE];
    const canDelete = resolvedRowPermissions[SECURITY_ACTIONS.DELETE];
    const canCopy = resolvedRowPermissions[SECURITY_ACTIONS.EDIT];
    const canInactivate = resolvedRowPermissions[SECURITY_ACTIONS.INACTIVATE];
    const canActivate = resolvedRowPermissions[SECURITY_ACTIONS.ACTIVATE];

    const actions: ReactElement<GridActionsCellItemProps>[] = [];

    if (isInEditMode) {
        actions.push(
            <GridActionsCellItem
                key="save"
                showInMenu={isMobile}
                icon={CHECK_ICON}
                label={saveLabel}
                onClick={() => editHandlers.handleEditCommit?.(params.id)}
                color="inherit"
                disabled={
                    typeof rowDetails?.isRowValid === 'function'
                        ? !rowDetails.isRowValid(rowDetails.activeRow.id)
                        : false
                }
            />,
            <GridActionsCellItem
                key="cancel"
                showInMenu={isMobile}
                icon={CLOSE_ICON}
                label={cancelLabel}
                className="textPrimary"
                onClick={() => editHandlers.handleEditCancel?.(params.id)}
                color="inherit"
            />
        );
    } else {
        if (canShare && onShare) {
            actions.push(
                <GridActionsCellItem
                    key="share"
                    showInMenu={isMobile}
                    icon={SHARE_ICON}
                    label={shareLabel}
                    onClick={() => onShare(params.row)}
                />
            );
        }

        if (canEdit && (editHandlers.onEdit || editHandlers.enableInlineEditing)) {
            if (!(editHandlers.enableInlineEditing && hideEditActionWhenInlineEditing)) {
                actions.push(
                    <GridActionsCellItem
                        key="edit"
                        showInMenu={isMobile}
                        icon={EDIT_ICON}
                        label={editLabel}
                        onClick={() =>
                            editHandlers.enableInlineEditing
                                ? editHandlers.handleEditClick?.(params.id)
                                : editHandlers.onEdit?.(params.id, params.row)
                        }
                    />
                );
            }
        }

        if (canCopy && copyHandlers.onCopy) {
            actions.push(
                <GridActionsCellItem
                    key="copy"
                    showInMenu={isMobile}
                    icon={
                        copyHandlers.copyTooltipTitle ? (
                            <Tooltip title={copyHandlers.copyTooltipTitle}>{COPY_ICON}</Tooltip>
                        ) : (
                            COPY_ICON
                        )
                    }
                    label={copyLabel}
                    onClick={() => copyHandlers.onCopy(params.row)}
                />
            );
        }

        if (
            canDelete &&
            (deleteHandlers.onDelete || deleteHandlers.enableInlineDelete || deleteHandlers.onDeleteHandle)
        ) {
            actions.push(
                <GridActionsCellItem
                    key="delete"
                    showInMenu={isMobile}
                    icon={DELETE_ICON}
                    label={deleteLabel}
                    onClick={() => deleteHandlers.handleDeleteClick?.(params.row)}
                />
            );
        }

        if ((canInactivate || canActivate) && onActivateOrInactivateHandle) {
            actions.push(
                <GridActionsCellItem
                    key="activate-inactivate"
                    showInMenu={isMobile}
                    icon={
                        <Chip
                            className="clickable"
                            size="small"
                            label={<I18n token={`action.${canInactivate ? 'inactive' : 'active'}`} />}
                            variant="outlined"
                            sx={{
                                width: isMobile ? '110px' : '100%',
                                justifyContent: 'center',
                                height: 20
                            }}
                        />
                    }
                    label=""
                    onClick={() => onActivateOrInactivateHandle(params.row, canInactivate, canActivate)}
                />
            );
        }
    }

    if (isInEditMode) {
        return actions;
    }

    const customActionCellItems = customActions?.map((action) => action(params)) ?? [];

    const visibilityAndSecurityGatedCustomRowActions = filterCustomRowActionsForVisibilityAndSecurity(
        customRowActions,
        {
            shouldUseCentralizedSecurityFramework,
            evaluateSecurity,
            resolvedRowPermissions,
            entityName,
            params
        }
    );

    const customRowActionCellItems = visibilityAndSecurityGatedCustomRowActions.map((action, index) => {
        const label = translateLabelToken(action.labelToken);
        const shouldShowTooltip = !action.hideTooltip && Boolean(label);

        return (
            <GridActionsCellItem
                key={`${action.securityAction}-${action.labelToken}-${index}`}
                icon={shouldShowTooltip ? <Tooltip title={label}>{action.icon}</Tooltip> : action.icon}
                label={label}
                showInMenu={isMobile}
                disabled={action.disabled}
                onClick={() => action.onClick(params.row)}
            />
        );
    });

    return [...actions, ...customActionCellItems, ...customRowActionCellItems];
};
